import { Spaces, useSpace } from '@components/hooks/use-space'
import { colors } from '@styles/colors'
import { typography } from '@styles/typography'
import { transition } from '@styles/utils'
import { PaginationProps } from 'baseui/pagination'
import classnames from 'classnames'
import range from 'lodash/range'
import { borderColor, borderRadius, borderStyle, borderWidth } from 'polished'
import React, { useMemo } from 'react'
import { useStyletron } from 'styletron-react'
import { Icon } from '../icon'

export interface Pagination1Props extends PaginationProps {
  className?: string
  spaceTop?: Spaces
  spaceBottom?: Spaces
}

export const Pagination = React.memo(
  ({
    numPages,
    currentPage,
    onNextClick,
    onPrevClick,
    onPageChange,
    className,
    spaceTop = 48,
    spaceBottom,
  }: Pagination1Props) => {
    const [css] = useStyletron()
    const { marginTop, marginBottom } = useSpace({ spaceTop, spaceBottom })

    const paginationNumbers = useMemo(() => {
      const pageNumbers = Math.floor((currentPage - 1) / 5) * 5 + 1
      return range(pageNumbers, pageNumbers + 5).filter(
        (num) => num < numPages + 1
      )
    }, [currentPage, numPages])

    return (
      <>
        <div
          className={classnames(
            'pagination',
            className,
            css({
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              ...marginTop,
              ...marginBottom,
            })
          )}
        >
          <Box onClick={onPrevClick} disabled={currentPage === 1}>
            <Icon
              type="ChevronLeft"
              size={12}
              color={currentPage === numPages ? colors.gray400 : colors.gray700}
            />
          </Box>
          {paginationNumbers.map((i) => (
            <Box
              key={i}
              selected={i === currentPage}
              onClick={
                onPageChange &&
                (() => onPageChange({ nextPage: i, prevPage: i }))
              }
            >
              {i}
            </Box>
          ))}

          <Box onClick={onNextClick} disabled={numPages === 0}>
            <Icon
              type="ChevronRight"
              size={12}
              color={currentPage === numPages ? colors.gray400 : colors.gray700}
            />
          </Box>
        </div>
      </>
    )
  }
)

interface BoxProps {
  selected?: boolean
  children: React.ReactNode
  onClick?: any
  disabled?: boolean
}

const Box = ({ children, selected, onClick, disabled }: BoxProps) => {
  const [css] = useStyletron()
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={css({
        backgroundColor: selected ? colors.blue50 : colors.white,
        cursor: 'pointer',
        width: '32px',
        height: '32px',
        ...borderRadius('top', '8px'),
        ...borderRadius('bottom', '8px'),
        ...borderWidth('1px'),
        ...borderStyle('none'),
        ...borderWidth('1px'),
        ...borderColor(selected ? colors.blue400 : colors.gray100),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...typography.ParagraphM,
        color: selected ? colors.blue400 : colors.gray700,
        ...transition(),
        [':disabled']: {
          color: colors.gray400,
          cursor: 'not-allowed',
          ':hover': {
            backgroundColor: colors.white,
          },
        },
        ':hover': {
          backgroundColor: selected ? colors.blue50 : colors.gray50,
        },
      })}
    >
      {children}
    </button>
  )
}
